import api from '../api/apiClient';
import { clearChatStorage } from '../ai-explorer/chatStorage';

/** After logout, successful login navigates here (pathname + search + hash). */
export const RETURN_AFTER_LOGIN_STORAGE_KEY = 'saf3ai_return_after_login';

export const authService = {
  /**
   * Read and clear the post-logout return path (used after the next login).
   * @returns {string|null} e.g. `/finops/budget?tab=1`
   */
  consumeReturnAfterLoginPath() {
    try {
      const p = sessionStorage.getItem(RETURN_AFTER_LOGIN_STORAGE_KEY);
      if (p) sessionStorage.removeItem(RETURN_AFTER_LOGIN_STORAGE_KEY);
      if (p && p.startsWith('/') && !p.startsWith('//')) return p;
    } catch (_) {
      /* ignore */
    }
    return null;
  },

  /**
   * Remember where to go after the next sign-in. ProtectedRoute calls this when
   * it sends a signed-out visitor to the login page; sessionStorage survives the
   * SSO round trip, where router state does not.
   * @param {string} path full path, e.g. `/device?code=ABCD-2345`
   */
  rememberReturnAfterLogin(path) {
    if (
      typeof path !== 'string' ||
      !path.startsWith('/') ||
      path.startsWith('//') ||
      path === '/' ||
      path.startsWith('/sso/')
    ) {
      return;
    }
    try {
      sessionStorage.setItem(RETURN_AFTER_LOGIN_STORAGE_KEY, path);
    } catch (_) {
      /* ignore */
    }
  },

  async login(email, password) {
    // Use v1 login endpoint so we receive both access + refresh tokens
    const response = await api.post('/auth/api/v1/auth/login', {
      username: email,
      password: password
    });
    return response.data;
  },

  /**
   * Answer the SAF3AI CLI's browser sign-in (`saf3ai auth login`: authorization
   * code + PKCE to a loopback listener). The server checks the client and the
   * loopback redirect, and returns where to send the browser next — the CLI's
   * listener on this machine, carrying a one-time code or `error=access_denied`.
   * @param {{clientId: string, redirectUri: string, state: string,
   *   codeChallenge: string, codeChallengeMethod?: string,
   *   decision: 'allow'|'deny'}} request
   * @returns {Promise<{redirect_url: string, email?: string}>}
   */
  async answerCliSignIn({ clientId, redirectUri, state, codeChallenge, codeChallengeMethod, decision }) {
    const response = await api.post('/auth/api/v1/auth/oauth/authorize', {
      client_id: clientId,
      redirect_uri: redirectUri,
      state: state || '',
      code_challenge: codeChallenge || '',
      code_challenge_method: codeChallengeMethod || 'S256',
      decision,
    });
    return response.data;
  },

  /**
   * Approve a `saf3ai auth login --no-browser` device-code request. Called from
   * the /device page after an already-logged-in user confirms the short code
   * the CLI printed. Requires the existing session token (attached by
   * apiClient's request interceptor) — that's what proves the human at the
   * keyboard is who they say they are; the device flow itself never asks
   * for a password.
   * @param {string} userCode e.g. "ABCD-1234"
   */
  async verifyDeviceCode(userCode) {
    const response = await api.post('/auth/api/v1/auth/device-code/verify', {
      user_code: userCode,
    });
    return response.data;
  },

  /**
   * Clear client session and optionally remember where to return after the next login.
   * @param {{ returnPath?: string }} [options] — full path like `/dashboard?x=1#y` (not login or sso)
   */
  async logout(options = {}) {
    const { returnPath } = options;
    let savedReturn = null;
    if (
      returnPath &&
      typeof returnPath === 'string' &&
      returnPath !== '/' &&
      !returnPath.startsWith('/sso/')
    ) {
      savedReturn = returnPath;
    }

    localStorage.removeItem('jwt_token');
    localStorage.removeItem('user');
    localStorage.removeItem('refresh_token');
    // Saf3AI Agent transcripts must not be readable by the next user of this browser.
    clearChatStorage();
    try {
      sessionStorage.removeItem('refresh_token');
    } catch (_) {
      /* ignore */
    }

    try {
      sessionStorage.clear();
      if (savedReturn) {
        sessionStorage.setItem(RETURN_AFTER_LOGIN_STORAGE_KEY, savedReturn);
      }
    } catch (_) {
      /* ignore */
    }

    // The logout endpoint is at /auth/logout (outside the /api sub-path)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);
    try {
      await fetch('/auth/logout', {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
      });
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('Logout API call failed:', error);
      }
    } finally {
      clearTimeout(timeoutId);
    }
  },

  async changePassword(currentPassword, newPassword) {
    const { authClient } = await import('../api/authClient');
    const response = await authClient.post('/user/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  },

  // =========================================================================
  // SSO Methods
  // =========================================================================

  async initiateSso(domain) {
    const response = await api.post('/auth/api/v1/auth/sso/initiate', { domain });
    return response.data;
  },

  async ssoCallbackOidc(code, state) {
    const response = await api.post('/auth/api/v1/auth/sso/callback/oidc', { code, state });
    return response.data;
  },

  async getSsoConfig() {
    const response = await api.get('/auth/api/v1/auth/sso/config');
    return response.data;
  },

  async updateSsoConfig(ssoProvider, ssoConfig, domain) {
    const response = await api.put('/auth/api/v1/auth/sso/config', {
      sso_provider: ssoProvider,
      sso_config: ssoConfig,
      ...(domain ? { domain } : {}),
    });
    return response.data;
  },

  async deleteSsoConfig() {
    const response = await api.delete('/auth/api/v1/auth/sso/config');
    return response.data;
  },

  async testSsoConnection(ssoProvider, ssoConfig) {
    const response = await api.post('/auth/api/v1/auth/sso/test', {
      sso_provider: ssoProvider,
      sso_config: ssoConfig,
    });
    return response.data;
  },

  // =========================================================================
  // Groups & RBAC Methods
  // =========================================================================

  async getGroups() {
    const response = await api.get('/auth/api/v1/groups');
    return response.data;
  },

  async createGroup(data) {
    const response = await api.post('/auth/api/v1/groups', data);
    return response.data;
  },

  async updateGroup(groupId, data) {
    const response = await api.put(`/auth/api/v1/groups/${groupId}`, data);
    return response.data;
  },

  async deleteGroup(groupId) {
    const response = await api.delete(`/auth/api/v1/groups/${groupId}`);
    return response.data;
  },

  async getGroupRoleMappings(groupId) {
    const response = await api.get(`/auth/api/v1/groups/${groupId}/role-mappings`);
    return response.data;
  },

  async addGroupRoleMapping(groupId, roleId, module = '*', teamId = null) {
    // teamId optional — when provided the mapping is scoped to that team's
    // resources; null/omitted preserves the legacy "all teams" semantics.
    const response = await api.post(`/auth/api/v1/groups/${groupId}/role-mappings`, {
      role_id: roleId,
      module: module,
      team_id: teamId,
    });
    return response.data;
  },

  async removeGroupRoleMapping(mappingId) {
    const response = await api.delete(`/auth/api/v1/groups/role-mappings/${mappingId}`);
    return response.data;
  },

  async getRoles() {
    const response = await api.get('/auth/api/v1/roles');
    return response.data;
  },

  async getRole(roleId) {
    const response = await api.get(`/auth/api/v1/roles/${roleId}`);
    return response.data;
  },

  async getRoleUsage(roleId) {
    const response = await api.get(`/auth/api/v1/roles/${roleId}/usage`);
    return response.data;
  },

  async getPermissionCatalog() {
    const response = await api.get('/auth/api/v1/permissions');
    return response.data;
  },

  async createRole(payload) {
    // payload: { name, description?, permissions: [{permission_id, module}] }
    const response = await api.post('/auth/api/v1/roles', payload);
    return response.data;
  },

  async updateRole(roleId, payload) {
    // payload: { name?, description?, permissions? }
    const response = await api.put(`/auth/api/v1/roles/${roleId}`, payload);
    return response.data;
  },

  async deleteRole(roleId) {
    const response = await api.delete(`/auth/api/v1/roles/${roleId}`);
    return response.data;
  },

  async suggestRole(description) {
    // POSTs a natural-language description to the AI suggester. Returns
    // { name, description, permissions: [{permission_id, module}] }
    // that the editor can seed. Backend returns 503 when the feature is
    // disabled (no ANTHROPIC_API_KEY) — callers should surface that
    // gracefully rather than treating it as a generic error.
    const response = await api.post('/auth/api/v1/roles/suggest', { description });
    return response.data;
  },

  // =========================================================================
  // User Role Assignment (Migration 006 — team-scoped)
  // =========================================================================

  async getUserRoles(userId) {
    const response = await api.get(`/auth/api/v1/users/${userId}/roles`);
    return response.data;
  },

  async assignRoleToUser(userId, roleId, { module = '*', team_id = null } = {}) {
    const response = await api.post(`/auth/api/v1/users/${userId}/roles`, {
      role_id: roleId,
      module,
      team_id,
    });
    return response.data;
  },

  async removeRoleFromUser(userId, roleId, { module = '*', team_id = null } = {}) {
    // Axios DELETE doesn't carry a JSON body by default — pass via `data:`
    const response = await api.delete(`/auth/api/v1/users/${userId}/roles`, {
      data: { role_id: roleId, module, team_id },
    });
    return response.data;
  },

  // =========================================================================
  // Team Membership (Migration 006)
  // =========================================================================

  async getTeams() {
    const response = await api.get('/auth/api/v1/teams');
    return response.data;
  },

  async getTeamMembers(teamId) {
    const response = await api.get(`/auth/api/v1/teams/${teamId}/members`);
    return response.data;
  },

  async addTeamMember(teamId, userId, teamRole = 'member') {
    const response = await api.post(`/auth/api/v1/teams/${teamId}/members`, {
      user_id: userId,
      team_role: teamRole,
    });
    return response.data;
  },

  async removeTeamMember(teamId, userId) {
    const response = await api.delete(`/auth/api/v1/teams/${teamId}/members/${userId}`);
    return response.data;
  },

  async getUserTeams(userId) {
    const response = await api.get(`/auth/api/v1/users/${userId}/teams`);
    return response.data;
  },

  // =========================================================================
  // SSO Group → Team mapping (Migration 006)
  // =========================================================================

  async getGroupTeamMappings(groupId) {
    const response = await api.get(`/auth/api/v1/groups/${groupId}/team-mappings`);
    return response.data;
  },

  async addGroupTeamMapping(groupId, teamId, teamRole = 'member') {
    const response = await api.post(`/auth/api/v1/groups/${groupId}/team-mappings`, {
      team_id: teamId,
      team_role: teamRole,
    });
    return response.data;
  },

  async removeGroupTeamMapping(mappingId) {
    const response = await api.delete(`/auth/api/v1/groups/team-mappings/${mappingId}`);
    return response.data;
  },

  // =========================================================================
  // Team-Role assignment (Migration 008)
  // Assign a role to a TEAM — members inherit the role's grants scoped to
  // the team. Mirrors the user-role endpoints; additive (never downgrades).
  // =========================================================================

  async getTeamRoles(teamId) {
    const response = await api.get(`/auth/api/v1/teams/${teamId}/roles`);
    return response.data;
  },

  async assignRoleToTeam(teamId, roleId, module = '*') {
    const response = await api.post(`/auth/api/v1/teams/${teamId}/roles`, {
      role_id: roleId,
      module,
    });
    return response.data;
  },

  async removeRoleFromTeam(teamId, roleId, module = '*') {
    // Axios DELETE doesn't carry a JSON body by default — pass via `data:`
    const response = await api.delete(`/auth/api/v1/teams/${teamId}/roles`, {
      data: { role_id: roleId, module },
    });
    return response.data;
  },

  getToken() {
    return localStorage.getItem('jwt_token');
  },

  isAuthenticated() {
    return !!this.getToken();
  },
};
